/**
 * Board #223 — the client half of the Pending Documents and Pending Payment gates.
 *
 * Every vocabulary here mirrors `backend/apps/renewals/service_request_workflow.py`.
 * None of it is the authority: the server recomputes the checklist on every read
 * and re-checks the gate on every write (`blocking_reason`), so a stale client
 * can only ever offer a control the server then refuses — it can never let one
 * through. What this file buys is that the screen offers the right controls to
 * the right person and never renders a state it did not get from the API.
 *
 * WHY THIS LIVES UNDER pages/servicing/ AND NOT services/renewals.ts
 * -----------------------------------------------------------------
 * `services/renewals.ts` is outside this task's file domain. See the handover
 * note about folding these calls back into it.
 */

import api from '@/services/api';

/** `service_request_workflow.MISSING | UPLOADED | APPROVED | REJECTED`. */
export type ChecklistStatus = 'missing' | 'uploaded' | 'approved' | 'rejected';

export interface RequiredDocument {
  code: string;
  label: string;
  status: ChecklistStatus;
  document_id: string | null;
  document_name: string;
  reason: string;
}

/** `service_request_workflow.payment_state()` — always present, never null. */
export interface ServicingPayment {
  status:
    | 'not_requested'
    | 'due'
    | 'initiated'
    | 'paid'
    | 'failed'
    | 'rejected'
    | 'verified';
  amount: string | null;
  currency: string;
  reference: string;
  method: string;
  requested_at: string | null;
  requested_by_name: string;
  paid_at: string | null;
  failure_reason: string;
  verified_at: string | null;
  verified_by_name: string;
  verification_note: string;
  rejection_reason: string;
}

export interface ServicingDocument {
  id: string;
  name: string;
  document_type: string;
  /** Authenticated route; `/policy/media/` URLs are no longer served in the payload. */
  download_url: string | null;
  file_size: number | null;
  uploaded_by: string | null;
  uploaded_by_name: string;
  review_status: ChecklistStatus;
  review_reason: string;
  reviewed_by_name: string;
  reviewed_at: string | null;
  created_at: string;
}

/** Roles `core.permissions.base.IsBrokerAdmin` admits — the only approvers. */
export const BROKER_ADMIN_ROLES = ['super_admin', 'country_admin', 'broker_admin'];

/** Roles `IsBrokerEmployee` admits — may attach documents, may not approve them. */
export const BROKER_SIDE_ROLES = [...BROKER_ADMIN_ROLES, 'broker_employee'];

/** `service_request_workflow.DOCUMENT_LABELS`. */
export const SERVICING_DOCUMENT_TYPES: { value: string; label: string }[] = [
  { value: 'address_proof', label: 'Address Proof' },
  { value: 'bank_details', label: 'Bank Account Details' },
  { value: 'bank_noc', label: 'Bank NOC / Hypothecation Letter' },
  { value: 'cancelled_cheque', label: 'Cancelled Cheque' },
  { value: 'form_29_30', label: 'Form 29 / Form 30' },
  { value: 'id_proof', label: 'ID Proof' },
  { value: 'nominee_id_proof', label: 'Nominee ID Proof' },
  { value: 'previous_policy_copy', label: 'Previous Policy Copy' },
  { value: 'rc_copy', label: 'RC Copy' },
  { value: 'request_letter', label: 'Signed Request Letter' },
  { value: 'sale_deed', label: 'Sale Deed / Delivery Note' },
  { value: 'supporting_document', label: 'Supporting Document' },
  { value: 'other', label: 'Other Document' },
];

const DOCUMENT_LABELS = new Map(SERVICING_DOCUMENT_TYPES.map((t) => [t.value, t.label]));

export function servicingDocumentLabel(code: string): string {
  return (
    DOCUMENT_LABELS.get(code) ??
    code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/** How the customer may report a payment. `verified` is deliberately absent. */
export const PAYMENT_OUTCOMES: { value: 'initiated' | 'success' | 'failed'; label: string }[] = [
  { value: 'initiated', label: 'I have started the payment' },
  { value: 'success', label: 'The payment went through' },
  { value: 'failed', label: 'The payment failed' },
];

export const PAYMENT_METHODS: { value: string; label: string }[] = [
  { value: 'upi', label: 'UPI' },
  { value: 'netbanking', label: 'Net Banking' },
  { value: 'card', label: 'Debit / Credit Card' },
  { value: 'neft', label: 'NEFT / IMPS / RTGS' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
];

export function paymentStatusLabel(status: ServicingPayment['status']): string {
  switch (status) {
    case 'not_requested':
      return 'No payment requested';
    case 'due':
      return 'Awaiting payment';
    case 'initiated':
      return 'Payment started';
    case 'paid':
      return 'Paid — awaiting verification';
    case 'failed':
      return 'Payment failed';
    case 'rejected':
      return 'Payment rejected by Broker Admin';
    case 'verified':
      return 'Verified by Broker Admin';
    default:
      return status;
  }
}

export function paymentBadgeVariant(
  status: ServicingPayment['status']
): 'success' | 'error' | 'warning' | 'info' | 'default' {
  switch (status) {
    case 'verified':
      return 'success';
    case 'failed':
    case 'rejected':
      return 'error';
    case 'paid':
    case 'initiated':
      return 'info';
    case 'due':
      return 'warning';
    default:
      return 'default';
  }
}

/**
 * Mirrors `ServiceRequestDocumentSerializer.ALLOWED_UPLOAD_TYPES` /
 * `MAX_UPLOAD_BYTES` in backend/apps/renewals/serializers.py. Deliberately the
 * same set the claims upload uses, so a customer is not told two different
 * stories about what they may attach.
 */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export const ALLOWED_UPLOAD_EXTENSIONS = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif',
  '.tif',
  '.tiff',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
];

export const UPLOAD_ACCEPT_ATTRIBUTE = ALLOWED_UPLOAD_EXTENSIONS.join(',');

export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The reason this file cannot be uploaded, or `null` if it can. */
export function validateServicingFile(file: File): string | null {
  if (!file.size) return `${file.name} is empty.`;
  if (file.size > MAX_UPLOAD_BYTES) {
    return `${file.name} is ${formatFileSize(file.size)}. The limit is ${
      MAX_UPLOAD_BYTES / 1048576
    } MB.`;
  }
  const dot = file.name.lastIndexOf('.');
  const extension = dot === -1 ? '' : file.name.slice(dot).toLowerCase();
  if (!ALLOWED_UPLOAD_EXTENSIONS.includes(extension)) {
    return `${extension || 'That file type'} is not a supported document. Upload a PDF, a photo (JPG, PNG, WEBP, HEIC or TIFF), or a Word/Excel document.`;
  }
  return null;
}

/**
 * Read `required_documents` off a detail payload, or `null` if it is not there.
 *
 * `null` and `[]` are different answers and the UI treats them differently:
 * `[]` means "this request type needs no paperwork", `null` means "this API did
 * not tell us" — which happens during a rolling deploy where the SPA is ahead of
 * gunicorn. Rendering an empty checklist for the second case would tell a
 * customer nothing is required when the truth is that we do not know.
 */
export function readChecklist(payload: unknown): RequiredDocument[] | null {
  const value = (payload as { required_documents?: unknown } | null)?.required_documents;
  return Array.isArray(value) ? (value as RequiredDocument[]) : null;
}

/** Same contract as `readChecklist`: `null` means the API did not report it. */
export function readPayment(payload: unknown): ServicingPayment | null {
  const value = (payload as { payment?: unknown } | null)?.payment;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  if (typeof (value as { status?: unknown }).status !== 'string') return null;
  return value as ServicingPayment;
}

/** The request's documents in the shape this screen needs; `[]` when absent. */
export function readWorkflowDocuments(payload: unknown): ServicingDocument[] {
  const value = (payload as { documents?: unknown } | null)?.documents;
  return Array.isArray(value) ? (value as ServicingDocument[]) : [];
}

const base = (requestId: string) => `/renewals/service-requests/${requestId}`;

export const serviceRequestWorkflowApi = {
  async uploadDocument(
    requestId: string,
    input: { file: File; documentType: string; name?: string }
  ): Promise<ServicingDocument> {
    const form = new FormData();
    form.append('file', input.file);
    form.append('document_type', input.documentType);
    if (input.name?.trim()) form.append('name', input.name.trim());
    const response = await api.post<ServicingDocument>(`${base(requestId)}/documents/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async deleteDocument(requestId: string, documentId: string): Promise<void> {
    await api.delete(`${base(requestId)}/documents/${documentId}/`);
  },

  /**
   * Pull the stored bytes through the authenticated route and hand them to the
   * browser.
   *
   * The public `/policy/media/` URL used to be handed out in the payload, which
   * put customers' ID proofs and cancelled cheques behind nothing at all. This
   * route applies the same scoping the list does.
   */
  async downloadDocument(requestId: string, document: ServicingDocument): Promise<void> {
    const response = await api.get(`${base(requestId)}/documents/${document.id}/download/`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(response.data as Blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = document.name || 'servicing-document';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /** Broker Admin only, server-side (`ServiceRequestDocumentViewSet.review`). */
  async reviewDocument(
    requestId: string,
    documentId: string,
    decision: 'approved' | 'rejected',
    reason: string
  ): Promise<void> {
    await api.post(`${base(requestId)}/documents/${documentId}/review/`, { decision, reason });
  },

  /** Broker Admin only. Moves the request to Pending Documents. */
  async requestDocuments(
    requestId: string,
    payload: { document_types?: string[]; note?: string }
  ): Promise<void> {
    await api.post(`${base(requestId)}/request_documents/`, payload);
  },

  /** Broker Admin only. Refuses while any required document is outstanding. */
  async approveDocuments(
    requestId: string,
    payload: { next_status?: string; note?: string }
  ): Promise<void> {
    await api.post(`${base(requestId)}/approve_documents/`, payload);
  },

  /** Broker Admin only. Sets the amount due and moves to Pending Payment. */
  async requestPayment(
    requestId: string,
    payload: { amount: string; currency?: string; note?: string }
  ): Promise<void> {
    await api.post(`${base(requestId)}/request_payment/`, payload);
  },

  /**
   * The CUSTOMER's half. Records what happened; never advances the request.
   * `outcome` has no 'verified' in it — that word belongs to `verifyPayment`.
   */
  async recordPayment(
    requestId: string,
    payload: {
      outcome: 'initiated' | 'success' | 'failed';
      reference?: string;
      method?: string;
      failure_reason?: string;
    }
  ): Promise<void> {
    await api.post(`${base(requestId)}/record_payment/`, payload);
  },

  /** Broker Admin only. The single door out of Pending Payment. */
  async verifyPayment(
    requestId: string,
    payload: { decision: 'approved' | 'rejected'; reason?: string; note?: string }
  ): Promise<void> {
    await api.post(`${base(requestId)}/verify_payment/`, payload);
  },
};
